// stores/auth.js
import { defineStore } from "pinia";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "@/firebase/config";
import api from "@/services/api"; // For backend API calls

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    loading: true,
    error: null,
    initialized: false,
    authReady: false,
  }),

  getters: {
    isAuthenticated: (state) =>
      !!state.user && state.initialized && state.authReady,
    isEmailVerified: (state) => state.user?.emailVerified || false,
    userDisplayName: (state) =>
      state.user?.displayName || state.user?.email?.split("@")[0] || "User",
  },

  actions: {
    async initialize() {
      // If already initialized and user exists, return immediately
      if (this.initialized && this.user) {
        this.authReady = true;
        return this.user;
      }

      // Check session storage for cached user
      const cachedUser = sessionStorage.getItem("myharf_user");
      if (cachedUser && !this.user) {
        try {
          this.user = JSON.parse(cachedUser);
          this.initialized = true;
          this.authReady = true;
          this.loading = false;
          console.log("📦 Using cached user from session storage");
          // Still try to refresh in background
          this.refreshUserInBackground();
          return this.user;
        } catch (e) {
          sessionStorage.removeItem("myharf_user");
        }
      }

      return new Promise((resolve) => {
        const authInstance = getAuth();

        // Set a timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
          if (!this.initialized) {
            console.warn(
              "⏰ Auth initialization timeout - continuing with cached data",
            );
            this.loading = false;
            this.initialized = true;
            this.authReady = true;
            resolve(this.user);
          }
        }, 5000);

        const unsubscribe = onAuthStateChanged(
          authInstance,
          async (firebaseUser) => {
            clearTimeout(timeoutId);

            try {
              if (firebaseUser) {
                // Try to get user from session storage first
                const cachedUserData =
                  sessionStorage.getItem("myharf_user_data");
                let userData = {};
                let fromCache = false;

                if (cachedUserData) {
                  try {
                    const parsed = JSON.parse(cachedUserData);
                    if (parsed.uid === firebaseUser.uid) {
                      userData = parsed;
                      fromCache = true;
                      console.log("📦 Using cached user data");
                    }
                  } catch (e) {}
                }

                // Only fetch from backend if online and no cache
                if (navigator.onLine && !fromCache) {
                  try {
                    // Fetch user profile from your backend API
                    const response = await api.get(
                      `/users/${firebaseUser.uid}`,
                    );
                    if (response.data.success) {
                      userData = response.data.data;
                      // Cache the data
                      sessionStorage.setItem(
                        "myharf_user_data",
                        JSON.stringify(userData),
                      );
                    }
                  } catch (error) {
                    console.warn(
                      "⚠️ Backend fetch failed, using cached data:",
                      error,
                    );
                    if (cachedUserData) {
                      try {
                        userData = JSON.parse(cachedUserData);
                        fromCache = true;
                      } catch (e) {}
                    }
                  }
                }

                this.user = {
                  ...firebaseUser,
                  ...userData,
                  displayName:
                    firebaseUser.displayName ||
                    userData.displayName ||
                    firebaseUser.email?.split("@")[0],
                  role: userData.role || "learner",
                };

                // Cache the full user
                sessionStorage.setItem(
                  "myharf_user",
                  JSON.stringify(this.user),
                );
              } else {
                this.user = null;
                sessionStorage.removeItem("myharf_user");
                sessionStorage.removeItem("myharf_user_data");
              }
            } catch (error) {
              console.error("Error fetching user data:", error);
              // If we have cached user, keep it
              if (!this.user && cachedUser) {
                try {
                  this.user = JSON.parse(cachedUser);
                } catch (e) {}
              }
            } finally {
              this.loading = false;
              this.initialized = true;
              this.authReady = true;
              resolve(this.user);
            }
          },
        );

        // Store unsubscribe function for cleanup
        this._unsubscribe = unsubscribe;
      });
    },

    async refreshUserInBackground() {
      // Refresh user data in background without blocking UI
      if (!this.user || !navigator.onLine) return;

      try {
        const response = await api.get(`/users/${this.user.uid}`);
        if (response.data.success) {
          const userData = response.data.data;
          this.user = {
            ...this.user,
            ...userData,
          };
          sessionStorage.setItem("myharf_user", JSON.stringify(this.user));
          sessionStorage.setItem("myharf_user_data", JSON.stringify(userData));
          console.log("🔄 User data refreshed in background");
        }
      } catch (error) {
        console.debug("Background refresh skipped:", error.message);
      }
    },

    async signUp(email, password, displayName) {
      try {
        this.error = null;
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        const { user } = userCredential;

        await updateProfile(user, { displayName });

        // Create user in your backend
        const response = await api.post("/users", {
          uid: user.uid,
          email: user.email,
          displayName: displayName,
          role: "learner",
        });

        if (!response.data.success) {
          throw new Error("Failed to create user in database");
        }

        const userData = response.data.data;

        this.user = {
          ...user,
          ...userData,
        };

        // Cache the user
        sessionStorage.setItem("myharf_user", JSON.stringify(this.user));
        sessionStorage.setItem("myharf_user_data", JSON.stringify(userData));

        // Send email verification
        await sendEmailVerification(user);

        return { success: true, user: this.user };
      } catch (error) {
        console.error("Sign up error:", error);
        this.error = error.message;
        return { success: false, error: error.message };
      }
    },

    async signIn(email, password) {
      try {
        this.error = null;
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password,
        );
        const { user } = userCredential;

        // Fetch user from backend
        const response = await api.get(`/users/${user.uid}`);
        const userData = response.data.success ? response.data.data : {};

        this.user = {
          ...user,
          ...userData,
          displayName:
            user.displayName || userData.displayName || email.split("@")[0],
        };

        // Cache the user
        sessionStorage.setItem("myharf_user", JSON.stringify(this.user));
        sessionStorage.setItem("myharf_user_data", JSON.stringify(userData));

        return { success: true, user: this.user };
      } catch (error) {
        console.error("Sign in error:", error);
        this.error = error.message;
        return { success: false, error: error.message };
      }
    },

    async signInWithGoogle() {
      try {
        this.error = null;
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
          prompt: "select_account",
        });
        const result = await signInWithPopup(auth, provider);
        const { user } = result;

        // Check if user exists in backend
        let userData = {};

        if (navigator.onLine) {
          try {
            const response = await api.get(`/users/${user.uid}`);
            if (!response.data.success) {
              // Create user in backend
              const createResponse = await api.post("/users", {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                role: "learner",
              });
              if (createResponse.data.success) {
                userData = createResponse.data.data;
              }
            } else {
              userData = response.data.data;
            }
          } catch (error) {
            console.warn("⚠️ Could not fetch/create user data:", error);
          }
        }

        this.user = {
          ...user,
          ...userData,
          displayName:
            user.displayName ||
            userData.displayName ||
            user.email?.split("@")[0],
        };

        // Cache the user
        sessionStorage.setItem("myharf_user", JSON.stringify(this.user));
        sessionStorage.setItem("myharf_user_data", JSON.stringify(userData));

        return { success: true, user: this.user };
      } catch (error) {
        console.error("Google sign in error:", error);
        this.error = error.message;
        return { success: false, error: error.message };
      }
    },

    async signOut() {
      try {
        await firebaseSignOut(auth);
        this.user = null;
        sessionStorage.removeItem("myharf_user");
        sessionStorage.removeItem("myharf_user_data");
        return { success: true };
      } catch (error) {
        console.error("Sign out error:", error);
        this.error = error.message;
        return { success: false, error: error.message };
      }
    },

    async resetPassword(email) {
      try {
        await sendPasswordResetEmail(auth, email);
        return { success: true };
      } catch (error) {
        console.error("Reset password error:", error);
        this.error = error.message;
        return { success: false, error: error.message };
      }
    },

    async updateUserProfile(data) {
      try {
        const user = auth.currentUser;
        if (!user) throw new Error("No user logged in");

        if (data.displayName && data.displayName !== user.displayName) {
          await updateProfile(user, { displayName: data.displayName });
        }

        // Update in backend
        const response = await api.put(`/users/${user.uid}`, data);

        if (!response.data.success) {
          throw new Error("Failed to update user in database");
        }

        const userData = response.data.data;

        this.user = {
          ...this.user,
          ...userData,
        };

        // Update cache
        sessionStorage.setItem("myharf_user", JSON.stringify(this.user));
        sessionStorage.setItem("myharf_user_data", JSON.stringify(userData));

        return { success: true };
      } catch (error) {
        console.error("Update profile error:", error);
        this.error = error.message;
        return { success: false, error: error.message };
      }
    },

    $dispose() {
      if (this._unsubscribe) {
        this._unsubscribe();
      }
    },
  },
});
